'use client'

/**
 * GA4 ecommerce / engagement event helpers.
 *
 * Ports the events the legacy Vue app fired via vue-gtag. Every event is scoped
 * to the GA4 property via `send_to` so it does not also hit the Google Ads tag
 * (both are configured on the same global gtag instance in app/layout.js).
 *
 * Events push straight onto dataLayer (see analytics/gtag.js) so they queue
 * correctly even before gtag.js finishes loading. All helpers no-op when
 * NEXT_PUBLIC_GA4_ID is unset (dev machines) or during SSR.
 */

import { GA4_ID } from '@/app/_lib/config/tracking'
import { gtag } from '@/app/_lib/analytics/gtag'

function send(name, params) {
    if (typeof window === 'undefined' || !GA4_ID) return
    gtag('event', name, { send_to: GA4_ID, ...params })
}

/**
 * Build a GA4 `items[]` entry, omitting empty optional fields.
 * @param {object} fields
 * @param {string|number} fields.id - sub_product_id
 * @param {string} fields.name - product name
 * @param {number|string} [fields.price] - unit price
 * @param {number|string} [fields.quantity=1]
 * @param {string} [fields.brand] - supplier/brand name
 * @param {string} [fields.variant] - variant string
 * @param {number|string} [fields.discount] - per-unit discount
 * @param {number} [fields.index] - position in list
 */
export function gaItem({ id, name, price, quantity = 1, brand, variant, discount, index }) {
    const item = {
        item_id: String(id),
        item_name: name || '',
    }
    if (typeof index === 'number') item.index = index
    if (price != null && price !== '') item.price = Number(parseFloat(price).toFixed(2))
    item.quantity = parseInt(quantity) || 1
    if (brand) item.item_brand = brand
    if (variant) item.item_variant = variant
    if (discount != null && parseFloat(discount) > 0) item.discount = Number(parseFloat(discount).toFixed(2))
    return item
}

/**
 * Map a cart item (cart.cartItems[n]) to a GA4 item. Unit price is the actual
 * paid per-unit price (line total / qty); per-unit discount is derived from the
 * original vs sale line total when the item is on sale.
 */
export function gaItemFromCartItem(cartItem, index) {
    const p = cartItem?.product || {}
    const qty = parseInt(cartItem?.quantity) || 1
    const lineTotal = parseFloat(cartItem?.total_price)
    const unitPrice = Number.isFinite(lineTotal) ? lineTotal / qty : parseFloat(p.retail_price)

    let discount
    const originalTotal = parseFloat(cartItem?.original_total)
    if (p.on_sale && Number.isFinite(originalTotal) && Number.isFinite(lineTotal) && originalTotal > lineTotal) {
        discount = (originalTotal - lineTotal) / qty
    }

    return gaItem({
        id: p.sub_product_id,
        name: p.sub_product_name,
        price: unitPrice,
        quantity: qty,
        brand: p.supplier_name,
        variant: p.variant?.variant_description,
        discount,
        index,
    })
}

/**
 * Map a full cart (cart.cartItems[]) to a GA4 items[] array. Empty if no items.
 */
export function gaItemsFromCart(cart) {
    if (!cart?.cartItems?.length) return []
    return cart.cartItems.map((cartItem, index) => gaItemFromCartItem(cartItem, index))
}

export function trackViewItem({ value, item }) {
    send('view_item', { currency: 'USD', value: parseFloat(value) || 0, items: [item] })
}

export function trackAddToCart({ value, item }) {
    send('add_to_cart', { currency: 'USD', value: parseFloat(value) || 0, items: [item] })
}

export function trackViewCart({ value, items }) {
    if (!items?.length) return
    send('view_cart', { currency: 'USD', value: parseFloat(value) || 0, items })
}

export function trackRemoveFromCart({ value, items }) {
    if (!items?.length) return
    send('remove_from_cart', { currency: 'USD', value: parseFloat(value) || 0, items })
}

/**
 * begin_checkout — fired when a user first enters the checkout flow.
 */
export function trackBeginCheckout({ value, items, coupon }) {
    if (!items?.length) return
    const params = { currency: 'USD', value: parseFloat(value) || 0, items }
    if (coupon) params.coupon = coupon
    send('begin_checkout', params)
}

/**
 * add_shipping_info — fired when the shipping address is confirmed.
 */
export function trackAddShippingInfo({ value, items, shippingTier }) {
    if (!items?.length) return
    const params = { currency: 'USD', value: parseFloat(value) || 0, items }
    if (shippingTier) params.shipping_tier = shippingTier
    send('add_shipping_info', params)
}

/**
 * add_payment_info — fired when the payment form is submitted.
 */
export function trackAddPaymentInfo({ value, items, paymentType }) {
    if (!items?.length) return
    const params = { currency: 'USD', value: parseFloat(value) || 0, items }
    if (paymentType) params.payment_type = paymentType
    send('add_payment_info', params)
}

export function trackSearch(searchTerm) {
    if (!searchTerm) return
    send('search', { search_term: searchTerm })
}

export function trackLogin(method = 'Hive') {
    send('login', { method })
}

export function trackSignUp(method = 'Hive') {
    send('sign_up', { method })
}

/**
 * Newsletter signup — keeps the legacy Vue app's event name and params
 * ('signup' / newsletter / successful) so historical GA4 reports stay
 * comparable.
 */
export function trackNewsletterSignup() {
    send('signup', { event_category: 'newsletter', event_label: 'successful' })
}
